import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import * as api from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasFeature } from '../../../infrastructure/feature'
import { ColumnDescriptor, DateRangePicker, NumberField, Select, TableItem } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'

type CompulsoryStock = {
    id: Guid
    productGroupId: string | null
    productId: string | null
    company: string
    start: Date
    end: Date
    volume: number
}

type Product = {
    id: string,
    code: string,
}

type ProductGroup = {
    id: string | null,
    name: string,
    productIds: string[]
}

type Company = {
    id: string
    code: string
    name: string
}

let emptyCompulsoryStock = () => {
    return {
        id: guid.empty,
        productGroupId: null,
        productId: null,
        company: null,
        start: null,
        end: null,
        volume: null
    }
}

type CompulsoryStockFilters = {
    productGroupName: string | null,
    productCode: string | null,
    companyCode: string | null,
    start: string | null,
    end: string | null,
    volume: string | null
}

let noFilters: CompulsoryStockFilters = {
    productGroupName: null,
    productCode: null,
    companyCode: null,
    start: null,
    end: null,
    volume: null
}


let toTableItem = (compulsoryStock: CompulsoryStock): TableItem<MasterDataItem<CompulsoryStock>> => {
    return {
        id: compulsoryStock.id,
        productGroupId: compulsoryStock.productGroupId,
        productId: compulsoryStock.productId,
        company: compulsoryStock.company,
        start: compulsoryStock.start,
        end: compulsoryStock.end,
        volume: compulsoryStock.volume,
        isModified: false
    }
}

let applyFilters = (compulsoryStocks: CompulsoryStock[], filters: CompulsoryStockFilters, productGroups: ProductGroup[], products: Product[]): CompulsoryStock[] => {
    if (filters.productGroupName)
        compulsoryStocks = compulsoryStocks.filter(x => productGroups.find(pg => pg.id === x.productGroupId)?.name?.toLowerCase().contains(filters.productGroupName!.toLowerCase()))

    if (filters.companyCode)
        compulsoryStocks = compulsoryStocks.filter(x => x.company.toLowerCase().contains(filters.companyCode!.toLowerCase()))

    if (filters.productCode)
        compulsoryStocks = compulsoryStocks.filter(x => products.find(p => p.id === x.productId)?.code?.toLowerCase().contains(filters.productCode!.toLowerCase()))

    if (filters.volume)
        compulsoryStocks = compulsoryStocks.filter(x => x.volume?.toString()?.contains(filters.volume!))

    if (filters.start)
        compulsoryStocks = compulsoryStocks.filter(x => moment(x.start).format('MM/DD/YYYY').contains(filters.start!))

    if (filters.end)
        compulsoryStocks = compulsoryStocks.filter(x => moment(x.end).format('MM/DD/YYYY').contains(filters.end!))

    return compulsoryStocks
}

function CompulsoryStockMasterData({ classes }: MuiProps) {
    let [productGroups, setProductGroups] = useState<ProductGroup[]>([])
    let [products, setProducts] = useState<Product[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [compulsoryStocks, setCompulsoryStock] = useState<CompulsoryStock[]>([])
    let [filters, setFilters] = useState<CompulsoryStockFilters>(noFilters)
    let excelGenerator = ExcelGeneratorContainer.useContainer()

    let load = async () => {
        let companys = await api.get<Company[]>('masterdata/compulsorystock/company')
        let compulsoryStocks = await api.get<CompulsoryStock[]>('masterdata/compulsorystock')

        if (hasFeature('CompulsoryStockByProductGroup')) {
            let productGroups = await api.get<ProductGroup[]>('masterdata/compulsorystock/productgroup')
            setProductGroups(productGroups)
        } else {
            let products = await api.get<Product[]>('masterdata/compulsorystock/product')
            setProducts(products)
        }

        setCompanys(companys)
        setCompulsoryStock(compulsoryStocks)
    }

    useEffect(() => {
        let effect = async () => {
            await load()
        }
        effect()
    }, [])

    let getItems = () => applyFilters(compulsoryStocks, filters, productGroups, products).map(toTableItem)

    let onExportExcel = async () => {
        excelGenerator.generate({
            filename: 'CompulsoryStock.xlsx',
            sheets: [{ name: 'CompulsoryStock', lines: createExcelLines(getItems(), columns) }]
        })
    }

    let onSave = async (item: CompulsoryStock) => {
        await api.post('masterdata/compulsorystock', item)
        await load()
        return true
    }

    let onDelete = async (ids: string[]) => {
        await api.del('masterdata/compulsorystock', { ids: ids })
        await load()
        return true
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<CompulsoryStock>>>[] = [
        hasFeature('CompulsoryStockByProductGroup')
            ? {
                name: t('admin.masterdata.compulsoryStock.productGroupName'), value: x => productGroups.find(pg => pg.id === x.productGroupId)?.name ?? '',
                columnFilter: { value: filters.productGroupName ?? '', onChange: (productGroupName: string) => setFilters({ ...filters, productGroupName }) }
            } : {
                name: t('admin.masterdata.compulsoryStock.productCode'), value: x => products.find(p => p.id === x.productId)?.code ?? '',
                columnFilter: { value: filters.productCode ?? '', onChange: (productCode: string) => setFilters({ ...filters, productCode }) }
            },
        {
            name: t('admin.masterdata.compulsoryStock.companyCode'), value: x => companys.find(c => c.code === x.company)?.name ?? '',
            columnFilter: { value: filters.companyCode ?? '', onChange: (companyCode: string) => setFilters({ ...filters, companyCode }) }
        },
        {
            name: t('admin.masterdata.compulsoryStock.startDate'), value: x => moment(x.start).format('MM/DD/YYYY'),
            columnFilter: { value: filters.start ?? '', onChange: (start: string) => setFilters({ ...filters, start }) }
        },
        {
            name: t('admin.masterdata.compulsoryStock.endDate'), value: x => moment(x.end).format('MM/DD/YYYY'),
            columnFilter: { value: filters.end ?? '', onChange: (end: string) => setFilters({ ...filters, end }) }
        },
        {
            name: t('admin.masterdata.compulsoryStock.volume'), value: x => x.volume.toString(),
            columnFilter: { value: filters.volume ?? '', onChange: (volume: string) => setFilters({ ...filters, volume }) }
        }
    ]

    let isManager = hasClaim(Claims.MasterdataCompulsoryStockLevel)

    return (
        <div className={classes.container}>
            <MasterDataShell
                headerLabel={t('admin.masterdata.compulsoryStock.compulsoryStocks')}
                itemLabel={t('admin.masterdata.compulsoryStock.compulsoryStock')}
                isManager={isManager}
                onExportExcel={onExportExcel}
                onNew={emptyCompulsoryStock}
                onDelete={onDelete}
                onSave={onSave}
                items={getItems()}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                            {hasFeature('CompulsoryStockByProductGroup') ?
                                <Select label={t('admin.masterdata.compulsoryStock.productGroups')}
                                    disabled={!isManager}
                                    value={selectedItem.productGroupId}
                                    choices={productGroups.map(x => ({ value: x.id!, text: x.name }))}
                                    onChange={val => { if (val) setSelectedItem({ ...selectedItem, productGroupId: val }) }} /> :
                                <Select label={t('admin.masterdata.compulsoryStock.product')}
                                    disabled={!isManager}
                                    value={selectedItem.productId}
                                    choices={products.map(x => ({ value: x.id, text: x.code }))}
                                    onChange={val => { if (val) setSelectedItem({ ...selectedItem, productId: val }) }} />
                            }

                            <Select label={t('admin.masterdata.compulsoryStock.company')}
                                disabled={!isManager}
                                value={selectedItem.company}
                                choices={companys.map(x => ({ value: x.code, text: x.name }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, company: val }) }} />

                            <DateRangePicker startDate={selectedItem.start} startLabel={t('admin.masterdata.compulsoryStock.startDate')}
                                onPeriodChange={period => setSelectedItem({ ...selectedItem, start: period.startDate, end: period.endDate })}
                                endDate={selectedItem.end} endLabel={t('admin.masterdata.compulsoryStock.endDate')}
                                autoReplace={true} disabled={!isManager} />

                            <NumberField label={t('admin.masterdata.compulsoryStock.volume')}
                                disabled={!isManager}
                                text={selectedItem.volume}
                                onChange={x => setSelectedItem({ ...selectedItem, volume: x })} />
                        </>
                    )}
            </MasterDataShell>
        </div>)
}

let styles = (theme) =>
    createStyles({
        container: { height: '100%' },
    })

export default withStyles(styles, muiOptions)(CompulsoryStockMasterData)